import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useResponsiveProp } from "@shopify/restyle";
import { BlurTargetView, BlurView } from "expo-blur";
import React, { Suspense, useEffect, useRef } from "react";
import { ImageBackground, StyleSheet, View } from "react-native";
import { useQueryLoader } from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";
import {
  Box,
  ScrollView,
} from "@whoosh/design-system/src/components/primitive";
import { images } from "@whoosh/design-system/src/theme/images";

import { AuthenticationLayoutLanguageSelectProvider } from "../../features/Authentication/components/AuthenticationLayout";
import { UnauthenticatedNavigatorParamList } from "../../navigators/UnauthenticatedStackNavigator/types";
import { setResetPasswordClubContext } from "../ResetPassword/setResetPasswordClubContext";
import {
  RegisterAccountContainer,
  RegisterAccountContainerQueryString,
} from "./components/RegisterAccountContainer";
import { RegisterAccountContainerQuery } from "./components/__generated__/RegisterAccountContainerQuery.graphql";

export const RegisterScreen = ({
  route,
}: NativeStackScreenProps<UnauthenticatedNavigatorParamList, "Register">) => {
  const { clubSlug, token } = route.params;
  const paddingTop = useResponsiveProp({ base: 36, md: 90 });

  const [queryRef, loadQuery] = useQueryLoader<RegisterAccountContainerQuery>(
    RegisterAccountContainerQueryString
  );
  useEffect(() => {
    let isCancelled = false;

    const loadRegistration = async () => {
      await setResetPasswordClubContext(clubSlug);

      if (!isCancelled && token) {
        loadQuery({ token, userContext: clubSlug });
      }
    };

    void loadRegistration();

    return () => {
      isCancelled = true;
    };
  }, [clubSlug, loadQuery, token]);

  return (
    <Layout>
      <ScrollView style={{ paddingTop }}>
        <Suspense fallback={<Loader />}>
          {queryRef ? (
            <RegisterAccountContainer token={token} queryRef={queryRef} />
          ) : null}
        </Suspense>
      </ScrollView>
    </Layout>
  );
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  const blurTargetRef = useRef<View | null>(null);
  const isMobileLayout = useResponsiveProp({
    base: 1,
    md: 0,
  });

  return (
    <AuthenticationLayoutLanguageSelectProvider>
      <View style={{ flex: 1 }}>
        <BlurTargetView ref={blurTargetRef} style={{ flex: 1 }}>
          <ImageBackground
            source={
              isMobileLayout
                ? images.initialBackground
                : images.desktopLandingBackground
            }
            resizeMode="cover"
            style={{ flex: 1 }}
          />
        </BlurTargetView>
        <BlurView
          blurMethod="dimezisBlurViewSdk31Plus"
          blurTarget={blurTargetRef}
          intensity={100}
          style={StyleSheet.absoluteFill}
        >
          <Box
            position="absolute"
            top={0}
            bottom={0}
            right={0}
            left={0}
            backgroundColor="surface-overlay"
            opacity={0.7}
          />
          {children}
        </BlurView>
      </View>
    </AuthenticationLayoutLanguageSelectProvider>
  );
};
